import React, { useMemo, useState } from "react";
import styled from "styled-components";
import Colors from "../../../styles/Colors";
import { H4 } from "../../../styles/Fonts";
import LabelWithHint from "../../../components/LabelWithHint";
import Button from "../../../components/Button";
import DepositModal from "../Modals/DepositModal";
import WrapEthModal from "../Modals/WrapEthModal";
import { getTruncNum } from "../../../utils/common";
import { PoolProps } from "../../../utils/constant/pools";
import { useWeb3React } from "@web3-react/core";
import useUserPoolSupply from "../../../hooks/useUserPoolSupply";
import useLendingPoolState from "../../../hooks/useLendingPoolState";
import WithdrawModal from "../Modals/WithdrawModal";
import useTokenBalance from "../../../hooks/useTokenBalance";
import useIsUnderCorrectNetwork from "../../../hooks/useIsUnderCorrectNetwork";
import SelectBridgeModal from "../../../components/SelectBridgeModal";

const MarketItem: React.FC<{
  pool: PoolProps;
}> = ({ pool }) => {
  const {
    lendingPoolAddress: poolAddress,
    tokenAddress,
    tokenDecimal,
    tokenSymbol,
    deployedChainId,
    tokenUrl,
  } = pool;

  const { account } = useWeb3React();
  const isUnderCorrectNetwork = useIsUnderCorrectNetwork();
  const isConnected = useMemo(
    () => !!account && isUnderCorrectNetwork,
    [account, isUnderCorrectNetwork]
  );

  const [showDepositModal, setShowDepositModal] = useState<boolean>(false);
  const [showWrapModal, setShowWrapModal] = useState<boolean>(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState<boolean>(false);
  const [bridgeModalIsShow, setBridgeModalIsShow] = useState<boolean>(false);

  const [isDepositing, setIsDepositing] = useState<boolean>(false);
  const [isWithdrawing, setIsWithdrawing] = useState<boolean>(false);
  const [isWrapping, setIsWrapping] = useState<boolean>(false);

  const { balance, earned, updateUserSupply } = useUserPoolSupply(poolAddress);
  const { totalBorrowed, totalSupply, lendingRate, updateLendingPoolState } =
    useLendingPoolState(poolAddress, deployedChainId);
  const { tokenBalance, updateTokenBalance } = useTokenBalance({
    tokenAddress,
    tokenDecimal,
  });

  return (
    <MarketRow>
      <Row>
        <TokenIcon src={tokenUrl} />
        <TokenName>{tokenSymbol}</TokenName>
      </Row>
      <Col>
        <LabelWithHint
          labelComponent={<HeadText>Lending Interest Rate</HeadText>}
          hintText="The current interest rate based on the utilization rate of Diamond Pool"
        />
        <GreenValueText>
          {lendingRate ? (lendingRate * 100).toFixed(2) : "-"}
          {" %"}
        </GreenValueText>
      </Col>
      <Col>
        <LabelWithHint
          labelComponent={<HeadText>Total Supply</HeadText>}
          hintText="The amount of total supplied ETH"
        />
        <ValueText>
          {totalSupply ? `${getTruncNum(totalSupply, 2)}` : "-"}
          {` ${tokenSymbol}`}
        </ValueText>
      </Col>
      <Col>
        <LabelWithHint
          labelComponent={<HeadText>Total Borrowed</HeadText>}
          hintText="The amount of total borrowed ETH"
        />
        <ValueText>
          {totalBorrowed ? `${getTruncNum(totalBorrowed, 2)}` : "-"}
          {` ${tokenSymbol}`}
        </ValueText>
      </Col>
      <Col>
        <LabelWithHint
          labelComponent={<HeadText>Utilization</HeadText>}
          hintText="The ratio of ETH that are being borrowed by Diamond users"
        />
        <ValueText>{`${
          totalSupply ? ((totalBorrowed / totalSupply) * 100).toFixed(2) : "-"
        } %`}</ValueText>
      </Col>
      <Col className="pool-s-2">
        <LabelWithHint
          labelComponent={<HeadText>Your Supply</HeadText>}
          hintText=""
        />
        <SupplyContainer>
          <SupplyLabel> {"Balance"}</SupplyLabel>
          <SupplyText>{`${getTruncNum(balance, 6)} ${tokenSymbol}`}</SupplyText>
          <SupplyLabel style={{ marginTop: "8px" }}> {"Earned"}</SupplyLabel>
          <SupplyText>{`${getTruncNum(earned, 6)} ${tokenSymbol}`}</SupplyText>
        </SupplyContainer>
      </Col>
      <BtnContainer>
        <div className="pool-s-1">
          <Button
            btnText="Deposit"
            btnType="primary"
            onClick={() => setShowDepositModal(true)}
            disabled={!isConnected || isDepositing}
            isLoading={isDepositing}
          />
        </div>
        <div className="pool-s-3">
          <Button
            btnText="Withdraw"
            btnType="secondary"
            onClick={() => setShowWithdrawModal(true)}
            disabled={!isConnected || isWithdrawing}
            isLoading={isWithdrawing}
          />
        </div>
        <Button
          btnText="Wrap ETH"
          btnType="secondary"
          onClick={() => setShowWrapModal(true)}
          disabled={!isConnected || isWrapping}
          isLoading={isWrapping}
        />
      </BtnContainer>
      <DepositModal
        isShowModal={showDepositModal}
        setShowModal={setShowDepositModal}
        pool={pool}
        updatePoolState={updateLendingPoolState}
        updateUserSupply={updateUserSupply}
        setIsDepositing={setIsDepositing}
        tokenBalance={tokenBalance}
        updateTokenBalance={updateTokenBalance}
        showBridgeModal={() => {
          setShowDepositModal(false);
          setBridgeModalIsShow(true);
        }}
      />
      <WrapEthModal
        isShowModal={showWrapModal}
        setShowModal={setShowWrapModal}
        setIsWrapping={setIsWrapping}
        tokenBalance={tokenBalance}
        updateTokenBalance={updateTokenBalance}
      />
      <WithdrawModal
        isShowModal={showWithdrawModal}
        setShowModal={setShowWithdrawModal}
        pool={pool}
        updatePoolState={updateLendingPoolState}
        updateUserSupply={updateUserSupply}
        balance={balance}
        setIsWithdrawing={setIsWithdrawing}
        updateTokenBalance={updateTokenBalance}
      />
      <SelectBridgeModal
        targetTokenAddress={tokenAddress}
        deployedChainId={deployedChainId}
        show={bridgeModalIsShow}
        onHide={() => {
          setBridgeModalIsShow(false);
          setShowDepositModal(true);
        }}
      />
    </MarketRow>
  );
};

export default MarketItem;

const HeadText = styled(H4)`
  display: block;
  color: ${Colors.gray3};
  font-size: 13px;
  line-height: 22px;
  @media (max-width: 768px) {
    font-size: 12px;
    line-height: 18px;
    font-weight: normal;
    white-space: nowrap;
  }
`;

const ValueText = styled(HeadText)`
  font-size: 16px;
  line-height: 24px;
  font-weight: 500;
  color: ${Colors.gray1};
  @media (max-width: 992px) {
    font-size: 14px;
    line-height: 22px;
  }
  @media (max-width: 768px) {
    font-size: 12px;
    line-height: 18px;
    width: 100%;
    text-align: right;
  }
`;

const SupplyText = styled(HeadText)`
  font-size: 14px;
  line-height: 22px;
  font-weight: 500;
  color: ${Colors.gray1};
  @media (max-width: 992px) {
    font-size: 14px;
    line-height: 22px;
  }
  @media (max-width: 768px) {
    font-size: 12px;
    line-height: 18px;
    width: 100%;
    text-align: right;
  }
`;

const SupplyLabel = styled.span`
  display: block;
  font-size: 12px;
  line-height: 18px;
  font-weight: 500;
  color: ${Colors.gray3};
  @media (max-width: 992px) {
    font-size: 14px;
    line-height: 22px;
  }
  @media (max-width: 768px) {
    font-size: 12px;
    line-height: 18px;
    width: 100%;
    text-align: right;
  }
`;

const GreenValueText = styled(ValueText)`
  font-family: Poppins, Open Sans, Goldman-Sans, Helvetica Neue, Helvetica,
    Roboto, PingFang TC, 微軟雅黑, Microsoft Yahei, sans-serif;
  color: ${Colors.green};
  font-weight: bold;
`;

const RowContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr) 130px;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  @media (max-width: 992px) {
    display: flex;
    flex-direction: column;
    flex: 1;
  }
`;

const MarketRow = styled(RowContainer)`
  background-color: ${Colors.backgroundGray4};
  padding: 18px 24px;
  border-radius: 8px;
  @media (max-width: 768px) {
    padding: 18px 18px;
  }
`;

const Col = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  div:first-child {
    display: none;
  }
  @media (max-width: 992px) {
    width: 100%;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 4px;
    div:first-child {
      display: flex;
    }
  }
`;

const BtnContainer = styled.div`
  /* width: 100px; */
  display: flex;
  flex-direction: column;
  gap: 12px;
  div:not(:first-child) {
    margin-top: 0px;
  }
  button {
    font-size: 14px;
    line-height: 18px;
  }
  @media (max-width: 992px) {
    width: 100%;
    margin-top: 12px;
    flex-direction: row;
  }
`;

const TokenIcon = styled.img`
  height: 24px;
  margin-right: 8px;
`;

const Row = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  position: relative;
  @media (max-width: 992px) {
    width: 100%;
    padding-bottom: 12px;
    margin-bottom: 12px;
    ::after {
      content: "";
      border-bottom: 0.5px solid ${Colors.gray4};
      width: calc(100% + 48px);
      position: absolute;
      top: 100%;
      left: -24px;
    }
  }
  @media (max-width: 768px) {
    ::after {
      content: "";
      border-bottom: 0.5px solid ${Colors.gray4};
      width: calc(100% + 36px);
      position: absolute;
      top: 100%;
      left: -18px;
    }
  }
`;

const TokenName = styled(ValueText)`
  @media (max-width: 992px) {
    font-size: 15px;
    font-weight: 700;
  }
  @media (max-width: 768px) {
    text-align: left;
  }
`;

const SupplyContainer = styled.div`
  @media (max-width: 992px) {
    text-align: right;
  }
`;
